/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { remoteSessionsAttachBinding } from "../funcs/remoteSessionsAttachBinding.js";
import { remoteSessionsCommitServerIdentityConfiguration } from "../funcs/remoteSessionsCommitServerIdentityConfiguration.js";
import { remoteSessionsCount } from "../funcs/remoteSessionsCount.js";
import { remoteSessionsDetachBinding } from "../funcs/remoteSessionsDetachBinding.js";
import { remoteSessionsDetachServerClientAssociation } from "../funcs/remoteSessionsDetachServerClientAssociation.js";
import { remoteSessionsGetServerIdentityImpact } from "../funcs/remoteSessionsGetServerIdentityImpact.js";
import { remoteSessionsList } from "../funcs/remoteSessionsList.js";
import { remoteSessionsListBindings } from "../funcs/remoteSessionsListBindings.js";
import { remoteSessionsListServerClientAssociations } from "../funcs/remoteSessionsListServerClientAssociations.js";
import { remoteSessionsRevoke } from "../funcs/remoteSessionsRevoke.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { CommitServerIdentityConfigurationResult } from "../models/components/commitserveridentityconfigurationresult.js";
import { CountRemoteSessionsResult } from "../models/components/countremotesessionsresult.js";
import { ListBindingsResponseBody } from "../models/components/listbindingsresponsebody.js";
import { ListServerClientAssociationsResult } from "../models/components/listserverclientassociationsresult.js";
import { PrincipalRemoteSessionBinding } from "../models/components/principalremotesessionbinding.js";
import { ServerIdentityImpactResult } from "../models/components/serveridentityimpactresult.js";
import {
  AttachBindingRequest,
  AttachBindingSecurity,
} from "../models/operations/attachbinding.js";
import {
  CommitServerIdentityConfigurationRequest,
  CommitServerIdentityConfigurationSecurity,
} from "../models/operations/commitserveridentityconfiguration.js";
import {
  CountRemoteSessionsRequest,
  CountRemoteSessionsSecurity,
} from "../models/operations/countremotesessions.js";
import {
  DetachBindingRequest,
  DetachBindingSecurity,
} from "../models/operations/detachbinding.js";
import {
  DetachServerClientAssociationRequest,
  DetachServerClientAssociationSecurity,
} from "../models/operations/detachserverclientassociation.js";
import {
  GetServerIdentityImpactRequest,
  GetServerIdentityImpactSecurity,
} from "../models/operations/getserveridentityimpact.js";
import {
  ListBindingsRequest,
  ListBindingsSecurity,
} from "../models/operations/listbindings.js";
import {
  ListRemoteSessionsRequest,
  ListRemoteSessionsResponse,
  ListRemoteSessionsSecurity,
} from "../models/operations/listremotesessions.js";
import {
  ListServerClientAssociationsRequest,
  ListServerClientAssociationsSecurity,
} from "../models/operations/listserverclientassociations.js";
import {
  RevokeRemoteSessionRequest,
  RevokeRemoteSessionSecurity,
} from "../models/operations/revokeremotesession.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class RemoteSessions extends ClientSDK {
  /**
   * attachBinding remoteSessions
   *
   * @remarks
   * Manage exact remote session attachments for an agent. Requires an ordinary human session, agent authorization authority and ownership of the upstream session. Never returns credentials.
   */
  async attachBinding(
    request: AttachBindingRequest,
    security?: AttachBindingSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PrincipalRemoteSessionBinding> {
    return unwrapAsync(remoteSessionsAttachBinding(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * commitServerIdentityConfiguration remoteSessions
   *
   * @remarks
   * Atomically configure identity for a Remote MCP-backed MCP server. The complete plan selects or creates a project Remote Identity Provider and links an existing client, creates a manual client, or automatically prefers CIMD over DCR. Existing-client linking requires mcp:write on the target and on every other MCP server sharing its user session issuer, because the client binding is keyed by issuer; creating a provider or client additionally requires project:write. Unsupported automatic registration returns manual_setup_required without changing local state.
   */
  async commitServerIdentityConfiguration(
    request: CommitServerIdentityConfigurationRequest,
    security?: CommitServerIdentityConfigurationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CommitServerIdentityConfigurationResult> {
    return unwrapAsync(remoteSessionsCommitServerIdentityConfiguration(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * countRemoteSessions remoteSessions
   *
   * @remarks
   * Count the distinct people (user subjects) holding a live remote_session minted through one remote_session_client. Scoped like listRemoteSessions: only sessions whose issuer, client and remote issuer are reachable from the caller's project count; API-key and anonymous subjects do not. A client shared by several MCP servers counts people across all of them.
   */
  async count(
    request: CountRemoteSessionsRequest,
    security?: CountRemoteSessionsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CountRemoteSessionsResult> {
    return unwrapAsync(remoteSessionsCount(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * detachBinding remoteSessions
   *
   * @remarks
   * Manage exact remote session attachments for an agent. Requires an ordinary human session, agent authorization authority and ownership of the upstream session. Never returns credentials.
   */
  async detachBinding(
    request: DetachBindingRequest,
    security?: DetachBindingSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(remoteSessionsDetachBinding(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * detachServerClientAssociation remoteSessions
   *
   * @remarks
   * Remove one MCP server's association with an upstream OAuth client. The client and its associations with other servers are untouched. When the server is the only consumer of its user session issuer, the association follows the issuer's client binding and is refused here with a conflict: turn off User Identity on the server instead. Requires mcp:write on the server.
   */
  async detachServerClientAssociation(
    request: DetachServerClientAssociationRequest,
    security?: DetachServerClientAssociationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(remoteSessionsDetachServerClientAssociation(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getServerIdentityImpact remoteSessions
   *
   * @remarks
   * Preview which other MCP servers and gateways a client binding change on a user session issuer would affect, across every project in the organization for an organization-level issuer. Each server's upstream authorization server is derived from the clients it can see (its own project's and organization-level ones), so a server is listed when that derivation changes (repoint or clear) or when a client it uses is replaced (resignin). A gateway is listed when a client it can see is unbound (client_removed). The target server is excluded. A change the commit would refuse, such as one that touches an organization-level client on an organization-level issuer, is refused here too. Requires mcp:write on mcp_server_id when given, otherwise project:write. Servers the caller cannot read are counted in hidden_server_count and never named.
   */
  async getServerIdentityImpact(
    request: GetServerIdentityImpactRequest,
    security?: GetServerIdentityImpactSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ServerIdentityImpactResult> {
    return unwrapAsync(remoteSessionsGetServerIdentityImpact(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRemoteSessions remoteSessions
   *
   * @remarks
   * List remote_sessions in the caller's project. Supplying both principal_id and user_session_issuer_id instead lists only the ordinary human caller's eligible sessions for an agent they own, without requiring project read permission. Both filters must be supplied together. access_token_encrypted and refresh_token_encrypted are never returned — only metadata (access_expires_at, refresh_expires_at, scopes).
   */
  async list(
    request?: ListRemoteSessionsRequest | undefined,
    security?: ListRemoteSessionsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PageIterator<ListRemoteSessionsResponse, { cursor: string }>> {
    return unwrapResultIterator(remoteSessionsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listBindings remoteSessions
   *
   * @remarks
   * Manage exact remote session attachments for an agent. Requires an ordinary human session, agent authorization authority and ownership of the upstream session. Never returns credentials.
   */
  async listBindings(
    request: ListBindingsRequest,
    security?: ListBindingsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListBindingsResponseBody> {
    return unwrapAsync(remoteSessionsListBindings(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listServerClientAssociations remoteSessions
   *
   * @remarks
   * List the upstream OAuth clients an MCP server is associated with. An association is a per-server configuration fact: one client can serve several servers, and server_count reports how many live servers share each client, since editing or rotating a shared client changes every one of them. Associations are recorded ahead of the serving path that will read them; until then the server keeps signing in through its user session issuer's bindings. Requires mcp:read on the server.
   */
  async listServerClientAssociations(
    request: ListServerClientAssociationsRequest,
    security?: ListServerClientAssociationsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListServerClientAssociationsResult> {
    return unwrapAsync(remoteSessionsListServerClientAssociations(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revokeRemoteSession remoteSessions
   *
   * @remarks
   * Drop a remote_session row. The next /mcp call by that principal triggers a fresh authn challenge.
   */
  async revoke(
    request: RevokeRemoteSessionRequest,
    security?: RevokeRemoteSessionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(remoteSessionsRevoke(
      this,
      request,
      security,
      options,
    ));
  }
}
